import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Clickable from "./Clickable";

/**
 * Header bảng "đầy đủ" — dựng theo cách HR Hub làm
 * (hrm-models-frontend: app/offboarding/hr/page.jsx + app/system/users/page.jsx):
 *
 *   Band 1 — tiêu đề + số dòng | ô search | menu hành động (3 chấm)
 *   Band 2 — các combobox filter, wrap tự do, kết bằng chip "Clear (n)"
 *   Mobile — band 2 gập lại sau 1 nút "Filters" có badge đếm
 *
 * Component này chỉ lo layout; filter cụ thể do trang truyền vào qua `filters`
 * để toolbar không cần biết gì về Payment Request.
 */

export const SearchBox = ({ value, onChange, placeholder = "Search...", className = "" }) => (
  <div className={`tw-flex tw-items-center tw-gap-2 tw-h-9 tw-px-2.5 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg focus-within:tw-border-geist-accent tw-transition-colors ${className}`}>
    <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
    <input
      type="text"
      autoComplete="off"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
    />
    {value && (
      <Clickable onClick={() => onChange("")} title="Clear search" className="tw-text-geist-fg-3 hover:tw-text-geist-fg tw-flex-shrink-0">
        <i className="bi bi-x-lg tw-text-[10px]" />
      </Clickable>
    )}
  </div>
);

SearchBox.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

/**
 * Menu hành động gộp — giữ header khỏi phình ngang mỗi lần thêm nút
 * (tương đương ToolbarActionsMenu của HR Hub).
 * items: [{ key, label, icon, onClick, disabled, primary } | { divider: true }]
 */
export const ToolbarActionsMenu = ({ items = [], label = "Actions", icon = "bi-three-dots" }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="tw-relative" ref={wrapRef}>
      <Clickable
        onClick={() => setOpen((o) => !o)}
        title={label}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100 hover:tw-text-geist-fg tw-transition-colors"
      >
        <i className={`bi ${icon} tw-text-sm`} />
      </Clickable>
      {open && (
        <div className="tw-absolute tw-right-0 tw-mt-1 tw-w-56 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-z-50 tw-overflow-hidden tw-py-1">
          {items.map((item, idx) => (item.divider ? (
            <div key={`div-${idx}`} className="tw-my-1 tw-h-px tw-bg-geist-border" />
          ) : (
            <Clickable
              key={item.key || item.label}
              disabled={item.disabled}
              onClick={() => { if (item.disabled) return; setOpen(false); item.onClick?.(); }}
              className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-[13px] tw-transition-colors ${
                item.disabled ? "tw-text-geist-fg-3" : "tw-text-geist-fg-2 hover:tw-bg-geist-100 hover:tw-text-geist-fg"
              }`}
            >
              {item.icon && <i className={`bi ${item.icon} tw-text-sm tw-w-4 tw-text-center tw-flex-shrink-0`} />}
              <span className="tw-truncate">{item.label}</span>
            </Clickable>
          )))}
        </div>
      )}
    </div>
  );
};

ToolbarActionsMenu.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.node,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    primary: PropTypes.bool,
    divider: PropTypes.bool,
  })),
  label: PropTypes.string,
  icon: PropTypes.string,
};

const TableToolbar = ({
  title,
  subtitle,
  search,
  onSearchChange,
  searchPlaceholder,
  actions,          // node — nút chính (vd "New request")
  actionsMenu,      // node — <ToolbarActionsMenu />
  segmented,        // node — dải tab
  filters,          // node — các <FilterDropdown />
  activeFilterCount = 0,
  onClearFilters,
  trailing,         // node — hiển thị cuối band 2 (vd "12 item(s)")
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  return (
    <div className="tw-px-4 sm:tw-px-6 tw-pt-4 tw-pb-3">
      {/* Band 1 — tiêu đề · search · actions */}
      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3 tw-mb-3">
        <div>
          <h1 className="tw-text-[22px] tw-font-semibold tw-tracking-tight tw-text-geist-fg tw-m-0">{title}</h1>
          {subtitle && <p className="tw-text-[13px] tw-text-geist-fg-3 tw-m-0 tw-mt-0.5">{subtitle}</p>}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {onSearchChange && (
            <SearchBox value={search} onChange={onSearchChange} placeholder={searchPlaceholder} className="tw-w-full md:tw-w-64" />
          )}
          {actionsMenu}
          {actions}
        </div>
      </div>

      {/* Band 2 — tab + filter */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        {segmented}

        {filters && (
          <>
            <div className="tw-hidden md:tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {filters}
              {activeFilterCount > 0 && (
                <Clickable
                  onClick={onClearFilters}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-rounded-geist-sm tw-text-[11px] tw-font-semibold tw-text-geist-error hover:tw-bg-geist-error/5 tw-transition-colors"
                >
                  <i className="bi bi-x-circle" /> Clear ({activeFilterCount})
                </Clickable>
              )}
            </div>

            {/* Mobile: gập toàn bộ filter sau 1 nút có badge đếm */}
            <div className="md:tw-hidden">
              <Clickable
                onClick={() => setMobileFiltersOpen((o) => !o)}
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-text-[11px] tw-font-semibold tw-transition-colors ${
                  activeFilterCount > 0
                    ? "tw-border-geist-accent tw-bg-geist-accent/5 tw-text-geist-accent"
                    : "tw-border-geist-border tw-bg-geist-bg tw-text-geist-fg-2"
                }`}
              >
                <i className="bi bi-funnel" /> Filters
                {activeFilterCount > 0 && (
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[16px] tw-h-4 tw-px-1 tw-rounded-xl tw-bg-geist-accent tw-text-white tw-text-[9px]">
                    {activeFilterCount}
                  </span>
                )}
              </Clickable>
            </div>
          </>
        )}

        {trailing && <span className="tw-ml-auto">{trailing}</span>}
      </div>

      {mobileFiltersOpen && filters && (
        <div className="md:tw-hidden tw-mt-2 tw-flex tw-flex-col tw-gap-2 tw-p-3 tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
          {filters}
          {activeFilterCount > 0 && (
            <Clickable onClick={onClearFilters} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-semibold tw-text-geist-error">
              <i className="bi bi-x-circle" /> Clear ({activeFilterCount})
            </Clickable>
          )}
        </div>
      )}
    </div>
  );
};

TableToolbar.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  actions: PropTypes.node,
  actionsMenu: PropTypes.node,
  segmented: PropTypes.node,
  filters: PropTypes.node,
  activeFilterCount: PropTypes.number,
  onClearFilters: PropTypes.func,
  trailing: PropTypes.node,
};

export default TableToolbar;
